import { useQuery } from '@tanstack/react-query'
import { Alert, Button, Divider, Flex, Table } from 'antd'
import axios from 'axios'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import queryString from 'query-string'
import { useState } from 'react'

import { useStocksServiceGetInventoryListEndpoint } from 'src/api/queries'
import {
  InventoryStatus,
  Permissions,
  ProductGroupCategory
} from 'src/api/requests'
import { ExportDocumentIcon } from 'src/assets'
import { ColumnTableFilter, PageBreadcrumbs, Text } from 'src/components'
import {
  useTableColumnsVisibility,
  useTablePagination,
  useUser
} from 'src/hooks'
import { dayMonthYearFormatWithDot } from 'src/libs'
import { useLocales } from 'src/locales'
import { parseToken } from 'src/utils'

import { StocAdministrationFilters } from '../components/stock'
import { useColumns } from '../hooks'
import { SelectedItemsState } from '../types'

const defaultHiddenColumns = [
  'internationalName',
  'producer',
  'measurementUnit',
  'in_date',
  'tvaRate',
  'total_tva',
  'info'
]

dayjs.extend(customParseFormat)

const MapValuesToIds = (data: SelectedItemsState) => {
  const { userData, hasPermission } = useUser()

  const isStockAdmin = hasPermission(Permissions.STOCKS_MENU_ACCESS_ISSUANCE)

  return {
    producerIds: data?.producer?.values || [],
    legalEntityIds: !isStockAdmin
      ? [userData?.currentLegalEntityId ?? '']
      : data?.legalEntityIds?.values,
    internationalNameIds: data?.internationalNameIds?.values || [],
    dateFrom: data?.inDate?.values?.[0],
    dateTo: data?.inDate?.values?.[1],
    searchTerm: data?.search?.values?.[0],
    producedDateTo: data?.productionDate?.values?.[1],
    producedDateFrom: data?.productionDate?.values?.[0],
    expirationDateTo: data?.expirationDate?.values?.[1],
    expirationDateFrom: data?.expirationDate?.values?.[0],
    statuses: data?.states?.values as InventoryStatus[],
    productGroupIds: data?.productGroupIds?.values,
    productGroupCategories: data?.productGroupCategories
      ?.values as ProductGroupCategory[]
  }
}

export const StockPage = () => {
  const [selectedItems, setSelectedItems] = useState<SelectedItemsState>({})

  const { t } = useLocales()
  const pagination = useTablePagination()

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    ...MapValuesToIds(selectedItems)
  }

  const { data, isLoading } = useStocksServiceGetInventoryListEndpoint(
    queryParams,
    [queryParams]
  )

  const { columns } = useColumns()

  const expiredMedicaments = data?.data?.filter((item) =>
    dayjs(item?.expirationDate, dayMonthYearFormatWithDot).isBefore(dayjs())
  )

  const outOfStockMedicaments = data?.data?.filter((item) => !item.inQuantity)

  const { newColumns, hiddenColumns, onVisibilityColumnsChange } =
    useTableColumnsVisibility(columns, defaultHiddenColumns)

  const options = columns?.map(({ key, title }) => ({
    label: title,
    value: key as string
  }))

  const exportQueryParams = {
    pageNumber: 1,
    ...MapValuesToIds(selectedItems)
  }

  const queryStringified = queryString.stringify(exportQueryParams, {
    arrayFormat: 'none',
    skipNull: true,
    skipEmptyString: true
  })

  const exportInventoryList = async () => {
    const response = await axios.get(
      `${import.meta.env.VITE_BASE_URL}/api/stocks/inventory/export?${queryStringified}`,
      {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${parseToken(localStorage.getItem('access_token'))}`,
          Accept:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      }
    )

    return response
  }

  const useExportInventoryList = (exportQueryParams: string) => {
    return useQuery({
      queryKey: ['exportInventory', exportQueryParams],
      queryFn: () => exportInventoryList(),
      enabled: false
    })
  }

  const { refetch, isLoading: isExportLoading } =
    useExportInventoryList(queryStringified)

  const onExport = async () => {
    try {
      const response = await refetch()

      if (!response?.data) {
        return
      }

      const blob = new Blob([response.data?.data], {
        type: response.data.data?.type
      })
      const url = window.URL.createObjectURL(blob)

      const a = document.createElement('a')
      a.href = url
      a.download = 'inventory.xlsx'
      document.body.appendChild(a)
      a.click()

      window.URL.revokeObjectURL(url)
    } catch {
      //empty
    }
  }

  return (
    <PageBreadcrumbs
      title={t('menu.stock')}
      items={[
        { title: t('menu.consumable_medicaments') },
        { title: t('menu.stock') }
      ]}
    >
      <Flex gap={16}>
        <StocAdministrationFilters
          pagination={pagination}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          hideProductGroupCategories
        />

        <ColumnTableFilter
          options={options}
          onChange={onVisibilityColumnsChange}
          hiddenColumns={hiddenColumns}
        />

        <Button
          icon={<ExportDocumentIcon />}
          loading={isExportLoading}
          onClick={onExport}
        >
          {t('common.export')}
        </Button>
      </Flex>

      <Divider />

      <Flex vertical gap={24}>
        <Flex vertical gap={8}>
          {outOfStockMedicaments?.map((item) => (
            <Alert
              key={item.id}
              type="warning"
              closable
              showIcon
              message={
                <Text>
                  {t('stock.out_of_stock')} -{' '}
                  <Text weight={700}>{item?.internationalName?.name}</Text>
                </Text>
              }
            />
          ))}

          {expiredMedicaments?.map((item) => (
            <Alert
              key={item.id}
              type="error"
              closable
              showIcon
              message={
                <Text>
                  {t('stock.expired')} -{' '}
                  <Text weight={700}>{item?.internationalName?.name}</Text>
                </Text>
              }
            />
          ))}
        </Flex>

        <Table
          rowKey="id"
          columns={newColumns}
          loading={isLoading}
          dataSource={data?.data}
          scroll={{ x: 'max-content' }}
          pagination={{ ...pagination, total: data?.totalCount }}
        />
      </Flex>
    </PageBreadcrumbs>
  )
}
